"use client";

import { useAuth } from "@/components/auth-provider";
import { ProviderAuthButton } from "@/components/auth/provider-auth-button";
import PasswordInput from "@/components/password-input";
import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from "@/components/ui/form";
import {
  InputGroup,
  InputGroupAddon,
  InputGroupInput,
} from "@/components/ui/input-group";
import { handleFormErrors } from "@/lib/form-errors";
import { cn } from "@/lib/utils";
import { zodResolver } from "@hookform/resolvers/zod";
import { IconMail } from "@tabler/icons-react";
import { useTransition } from "react";
import { useForm } from "react-hook-form";
import { toast } from "sonner";
import { signup } from "../signin/actions";
import { SignupFormData, signupSchema } from "../signin/schema";

type Props = {
  afterSignup?: () => void;
};

function SignupForm({ afterSignup }: Props) {
  const { refreshAuth } = useAuth();
  const [isPending, startTransition] = useTransition();

  const form = useForm<SignupFormData>({
    resolver: zodResolver(signupSchema),
    defaultValues: {
      email: "",
      password: "",
    },
  });

  const handleSignup = (data: SignupFormData) => {
    startTransition(async () => {
      const result = await signup(data);

      if (result.success) {
        await refreshAuth();
        toast.success(
          "Account created! Please check your email to verify your account and complete signup.",
        );
        afterSignup?.();
        form.reset();
      } else {
        if (result.errors) {
          handleFormErrors(form.setError, result.errors);
        }
      }
    });
  };

  return (
    <div>
      <Form {...form}>
        <form onSubmit={form.handleSubmit(handleSignup)} className="space-y-4">
          <FormField
            control={form.control}
            name="email"
            render={({ field }) => (
              <FormItem>
                <FormControl>
                  <InputGroup>
                    <InputGroupInput
                      {...field}
                      type="email"
                      disabled={isPending}
                      placeholder="Enter your email"
                    />
                    <InputGroupAddon>
                      <IconMail className="h-4 w-4 text-zinc-400" />
                    </InputGroupAddon>
                  </InputGroup>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="password"
            render={({ field }) => (
              <FormItem>
                <FormControl>
                  <PasswordInput
                    {...field}
                    disabled={isPending}
                    autoComplete="password"
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <Button
            type="submit"
            className={cn(
              "h-11 w-full font-medium text-white",
              "bg-linear-to-r from-pink-500 to-purple-600 hover:from-pink-600 hover:to-purple-700",
              "disabled:cursor-not-allowed disabled:opacity-50",
            )}
            disabled={isPending}
          >
            {isPending ? "Signing up..." : "Sign Up"}
          </Button>
        </form>
      </Form>

      <ProviderAuthButton disabled={isPending} />
    </div>
  );
}

export default SignupForm;
